<template>
  <ValidationObserver ref="observer" v-slot="{ passes }">
    <div class="font-16 dialog-retrieve pb-0" ref="formContainer">
      <v-form ref="submitForm" @submit.prevent="passes(onSubmit)">
        <v-alert v-if="retrieveQuoteError === true" color="var(--red-100)">
          <div class="d-flex flex-no-wrap error-message">
            <div class="mr-3">
              <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
            </div>
            <div>
              <span
                >Sorry, we couldn't retrieve a quote with these details. Please
                try again.</span
              >
            </div>
          </div>
        </v-alert>
        <p class="dialog-text">
          If you've had a quote from us before and got a quote reference, please
          enter your details below to retrieve your securely stored quote.
        </p>
        <div class="input-base">
          <InputBase
            class="quote-reference resize-base-input"
            :question="quoteReferenceQuestion"
          />
        </div>
        <div class="input-base">
          <InputBase
            class="post-code resize-base-input"
            :question="postCodeQuestion"
          />
        </div>
        <div>
          <HomeBaseInputDate
            labelClass="font-weight-medium font-18 mb-6px"
            label="Date of birth"
            class="resize-base-input"
            :hide-details="false"
            :height="48"
            :validation="dateOfBirthValidation"
            :questionObject="dateOfBirth"
          />
        </div>
        <div class="button-container">
          <div style="width: 420px">
            <BaseButton
              text="RETRIEVE YOUR QUOTE"
              rounded
              block
              type="submit"
              class="font-weight-bold"
              loadingText="RETRIEVING..."
              height="60"
              :color="'psorange'"
              :textColor="'#000000'"
              :fontSize="'font-20'"
              :loading="isRetrieving"
            />
          </div>
        </div>
      </v-form>
    </div>
  </ValidationObserver>
</template>

<script>
import {
  checkIfQuoteNeedShowEligibility,
  isQuoteExpired,
} from "@/common/quotes";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import HomeBaseInputDate from "@/components/HomeComponents/HomeBaseComponents/HomeBaseInputDate.vue";
import InputBase from "@/components/Inputs/InputBase.vue";
import { AGE_FOR_APPLICATION } from "@/constants/common.constants";
import { POST_CODE } from "@/constants/regex-patterns.constants";
import quoteService from "@/services/quote.service";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "DialogContentRetrieveQuote",
  components: {
    BaseButton,
    HomeBaseInputDate,
    InputBase,
    ValidationObserver,
  },
  props: {
    quoteReference: {
      type: String,
      default: null,
    },
    pageProductId: {
      type: Array,
      default: null,
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["getAnswerObject"]),
  },
  data() {
    return {
      isRetrieving: false,
      dateOfBirth: { Value: null },
      retrieveQuoteError: null,
      dateOfBirthValidation: {
        Rules: [
          "ruleRequired",
          "ruleDateValid",
          "ruleRegexFormat",
          "ruleAgeInRange",
        ],
        RuleProperties: {
          Required: {
            ErrorMsg: "Date of birth must not be blank.",
          },
          DateValid: {
            ErrorMsg: "Date of birth is not valid.",
          },
          AgeInRange: {
            ErrorMsg: "Date of birth must be between 18 and 110 years old.",
            MaxValue: AGE_FOR_APPLICATION.max,
            MinValue: AGE_FOR_APPLICATION.min,
          },
          RegexFormat: {
            Value: "^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$",
            ErrorMsg: "Date of birth is not valid.",
          },
        },
      },
      postCodeQuestion: {
        DataUiid: "Postcode",
        InterfaceKey: "Postcode",
        Text: "Property postcode",
        IsVisible: true,
        Value: "",
        DisplayType: "Question",
        AnswerControlAttributeType: "text",
        HideDetails: false,
        Validation: {
          Rules: ["ruleRequired", "ruleMaxLength", "ruleRegexFormat"],
          RuleProperties: {
            Required: {
              ErrorMsg: "Property postcode must not be blank.",
            },
            MaxLength: {
              Value: 10,
              ErrorMsg: "is not valid.",
            },
            RegexFormat: {
              Value: POST_CODE,
              ErrorMsg: "Property postcode is not valid.",
            },
          },
        },
      },
      quoteReferenceQuestion: {
        DataUiid: "QuoteReference",
        InterfaceKey: "QuoteReference",
        Text: "Quote reference",
        IsVisible: true,
        Value: this.quoteReference,
        DisplayType: "Question",
        AnswerControlAttributeType: "text",
        HideDetails: false,
        Validation: {
          Rules: ["ruleRequired", "ruleMaxLength", "ruleRegexFormat"],
          RuleProperties: {
            Required: {
              ErrorMsg: "Quote reference must not be blank.",
            },
            MaxLength: {
              Value: 24,
              ErrorMsg: "may not be greater than 24 characters.",
            },
            RegexFormat: {
              Value: "^\\s*PSL-[a-z]{2,9}-[0-9]{6,10}\\s*$",
              ErrorMsg: "Quote reference is not valid.",
              Flags: "i",
            },
          },
        },
      },
    };
  },
  watch: {
    "quoteReferenceQuestion.Value"() {
      this.resetRetrieveQuoteError();
    },
    "postCodeQuestion.Value"() {
      this.resetRetrieveQuoteError();
    },
    "dateOfBirth.Value"() {
      this.resetRetrieveQuoteError();
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setQuoteRequestIdAfterRetrieve",
      "setIsOriginalQuoteExpired",
    ]),
    resetRetrieveQuoteError() {
      if (this.retrieveQuoteError === true) {
        this.retrieveQuoteError = false;
      }
    },
    async onSubmit() {
      const isValid = await this.$refs.submitForm.validate();
      if (!isValid) {
        return;
      }
      try {
        this.retrieveQuoteError = false;
        this.isRetrieving = true;
        const result = await quoteService.retrieveQuoteByQuoteReference(
          this.quoteReferenceQuestion.Value.trim(),
          this.postCodeQuestion.Value.trim(),
          this.dateOfBirth.Value,
          this.pageProductId
        );
        if (!result.ContinuationUrl) {
          this.retrieveQuoteError = true;
          return;
        }

        this.setIsOriginalQuoteExpired(isQuoteExpired(result));
        const needShowEligibility = checkIfQuoteNeedShowEligibility(
          "HomeEligibility",
          result
        );

        if (needShowEligibility) {
          this.$emit("showEligibility", result.ContinuationUrl);
          return;
        }
        window.location.href = result.ContinuationUrl;
      } catch (ex) {
        this.retrieveQuoteError = true;
        this.$refs.formContainer.scrollIntoView({
          block: "start",
          scrollBehavior: "smooth",
        });
      } finally {
        this.isRetrieving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
tbody {
  tr:hover {
    background-color: transparent !important;
  }
}

tr:nth-of-type(odd) {
  background-color: #f5f5f5 !important;
}

.btn-file-downloaded {
  border: 1px solid white;
}

.input-label {
  font-size: 18px;
  font-weight: bold;
}

.dialog-retrieve {
  padding: 20px 40px 40px 37px;
}

.dialog-text {
  font-size: 16px;
  letter-spacing: 0.4px;
  color: #272727;
  margin-bottom: 31px;
}

.mg-bt-5px {
  margin-bottom: 5px;
}

.something-went-wrong {
  text-align: center;
  font-weight: 500;
  color: #ff5252;
  padding-bottom: 0.5rem;
}

.input-base {
  margin-bottom: 10px;
  :deep(.col-12.col-md-6.pa-0.quote-reference) {
    max-width: 100%;
  }

  :deep(.col-12.col-md-6.pa-0.post-code) {
    max-width: 274px;
  }
}

.date-label {
  margin-bottom: 6px;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 14px;
  overflow: auto;
}

@media screen and (max-width: $grid-breakpoints-sm) {
  .dialog-retrieve {
    padding: 19px 24px 40px 22px;
  }
}
</style>
